import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Upload, Download } from 'lucide-react';
import { saveKey, getKey, deleteKey } from '../lib/keys';
import type { Provider } from '../lib/db';

interface KeyRow { provider: Provider; label: string; placeholder: string; helpUrl: string; }

const ROWS: KeyRow[] = [
  { provider: 'gemini',     label: 'Google Gemini',     placeholder: 'AIza…',         helpUrl: 'https://aistudio.google.com/app/apikey' },
  { provider: 'anthropic',  label: 'Claude',            placeholder: 'sk-ant-…',      helpUrl: 'https://console.anthropic.com/settings/keys' },
  { provider: 'byteplus',   label: 'BytePlus ModelArk', placeholder: 'sk-…',          helpUrl: 'https://console.byteplus.com/ark' },
  { provider: 'openai',     label: 'OpenAI',            placeholder: 'sk-…',          helpUrl: 'https://platform.openai.com/api-keys' },
  { provider: 'xai',        label: 'xAI (Grok)',        placeholder: 'xai-…',         helpUrl: 'https://console.x.ai' },
  { provider: 'elevenlabs', label: 'ElevenLabs',        placeholder: 'xi-…',          helpUrl: 'https://elevenlabs.io/app/settings/api-keys' },
  { provider: 'meshy',      label: 'Meshy',             placeholder: 'ms-…',          helpUrl: 'https://docs.meshy.ai/get-started/authentication' },
  { provider: 'uniblock',   label: 'Uniblock (on-chain)', placeholder: 'uni-…',       helpUrl: 'https://dashboard.uniblock.dev' }
];

export default function ApiKeysModal({ onClose }: { onClose(): void }) {
  const [vals, setVals] = useState<Record<Provider, string>>({ gemini: '', anthropic: '', byteplus: '', openai: '', xai: '', elevenlabs: '', meshy: '', uniblock: '' });
  const [present, setPresent] = useState<Record<Provider, boolean>>({ gemini: false, anthropic: false, byteplus: false, openai: false, xai: false, elevenlabs: false, meshy: false, uniblock: false });
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    (async () => {
      const out: any = {};
      for (const r of ROWS) out[r.provider] = !!(await getKey(r.provider));
      setPresent(out);
    })();
  }, []);

  async function save() {
    setSaving(true);
    try {
      for (const r of ROWS) {
        const v = vals[r.provider]?.trim();
        if (v) await saveKey(r.provider, v);
      }
      onClose();
    } finally { setSaving(false); }
  }

  async function remove(p: Provider) {
    await deleteKey(p);
    setPresent(prev => ({ ...prev, [p]: false }));
  }

  const handleExport = async () => {
    try {
      const keysObj: Record<string, string> = {};
      for (const r of ROWS) {
        const value = vals[r.provider]?.trim() || await getKey(r.provider);
        if (value) {
          keysObj[r.provider] = value;
        }
      }
      
      if (Object.keys(keysObj).length === 0) {
        setStatusMsg("No keys found to export.");
        setTimeout(() => setStatusMsg(''), 3000);
        return;
      }
      
      const blob = new Blob([JSON.stringify(keysObj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'protolive_keys.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setStatusMsg("Keys exported successfully.");
      setTimeout(() => setStatusMsg(''), 3000);
    } catch (e) {
      console.error(e);
      setStatusMsg("Failed to export keys.");
      setTimeout(() => setStatusMsg(''), 3000);
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const content = evt.target?.result as string;
          const parsed = JSON.parse(content);
          if (parsed && typeof parsed === 'object') {
            const updatedVals = { ...vals };
            const updatedPresent = { ...present };
            let importedCount = 0;
            
            for (const r of ROWS) {
              const k = parsed[r.provider] || parsed[r.provider.toUpperCase()];
              if (k && typeof k === 'string') {
                const keyStr = k.trim();
                if (keyStr) {
                  await saveKey(r.provider, keyStr);
                  updatedVals[r.provider] = ''; 
                  updatedPresent[r.provider] = true;
                  importedCount++;
                }
              }
            }
            
            if (importedCount > 0) {
              setVals(updatedVals);
              setPresent(updatedPresent);
              setStatusMsg(`Secured ${importedCount} imported key(s).`);
              setTimeout(() => setStatusMsg(''), 4000);
            } else {
              setStatusMsg("No valid keys found in JSON.");
              setTimeout(() => setStatusMsg(''), 3000);
            }
          }
        } catch (err) {
          console.error(err);
          setStatusMsg("Failed parsing file as JSON.");
          setTimeout(() => setStatusMsg(''), 3000);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="modal-backdrop"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ type: "spring", damping: 30, stiffness: 350 }}
        className="modal glass glass--strong"
        onClick={e => e.stopPropagation()}
      >
        <div style={{ float: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button 
            type="button" 
            className="btn btn--ghost text-white/70 hover:text-white"
            style={{ padding: '0 4px', minHeight: 'auto', height: '24px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px' }}
            onClick={handleImport}
            title="Import Keys from JSON"
          >
            <Upload size={12} /> <span style={{ fontSize: '9px', opacity: 0.8 }} className="header__btn-text">import</span>
          </button>
          <button 
            type="button" 
            className="btn btn--ghost text-white/70 hover:text-white"
            style={{ padding: '0 4px', minHeight: 'auto', height: '24px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px' }}
            onClick={handleExport}
            title="Export Keys to JSON file"
          >
            <Download size={12} /> <span style={{ fontSize: '9px', opacity: 0.8 }} className="header__btn-text">export</span>
          </button>
          <button className="btn btn--ghost" style={{ padding: '0 4px', minHeight: 'auto', height: '24px', fontSize: '9px' }} onClick={onClose}>close</button>
        </div>

        <h2 className="sc" style={{ marginTop: 0 }}>your api keys</h2>
        
        {statusMsg && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '11px',
            textAlign: 'center',
            marginBottom: '14px',
            fontWeight: 500
          }}>
            {statusMsg}
          </div>
        )}

        <p style={{ fontSize: 12, color: 'var(--fg-dim)', marginBottom: 18 }}>
          Stored encrypted in this browser only. Your keys are forwarded directly to the
          provider through our serverless proxy and never logged.
        </p>
        <div className="col" style={{ gap: 14 }}>
          {ROWS.map(r => (
            <div key={r.provider} className="field">
              <label>
                {r.label}
                {present[r.provider] && <span style={{ color: 'var(--accent)', marginLeft: 8 }}>● saved</span>}
              </label>
              <div className="row">
                <input
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={r.placeholder}
                  value={vals[r.provider]}
                  onChange={e => setVals(v => ({ ...v, [r.provider]: e.target.value }))}
                />
                {present[r.provider] && (
                  <button className="btn btn--ghost" onClick={() => remove(r.provider)}>delete</button>
                )}
              </div>
              <a className="sc" style={{ fontSize: 10, color: 'var(--fg-dim)' }} href={r.helpUrl} target="_blank" rel="noreferrer">
                get a key →
              </a>
            </div>
          ))}
        </div>
        <hr className="divider" />
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn--ghost" onClick={onClose}>cancel</button>
          <button className="btn btn--primary" disabled={saving} onClick={save}>{saving ? 'saving…' : 'save keys'}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
